import React, {useState} from "react";
import {
  Box,
  Circle,
  Flex,
  Stack,
  Text,
  useDisclosure,
  useMediaQuery,
  Button
} from "@chakra-ui/react";

import {FormattedMessage, useIntl} from "react-intl";
import {CgClose} from "react-icons/cg";
import APLink from "../../../components/APLink";
import {AiOutlineInfoCircle} from "react-icons/ai";
import {MathProblem, OperatorModel} from "../../../types";
import {intlNumber} from "../../../utils";
import APCommonDialog from "../../../components/APCommonDialog";
import {GrPowerReset} from "react-icons/gr";
import APIconButton from "../../../components/APIconButton";

interface ExerciseScreenProps {
  onOpenAssistance: () => void;
  currentProblem: MathProblem;
  resetExercise: () => void;
}

function InstructionSidebar(props: ExerciseScreenProps) {
  const { onOpenAssistance, currentProblem, resetExercise } = props;

  const intl = useIntl();
  const [isLargeScreen] = useMediaQuery("(min-width: 764px)");
  const [mobileInstructionOpen, setMobileInstructionOpen] = useState<boolean>(false);

  const {
    isOpen: instructionOpen,
    onOpen: onOpenInstruction,
    onClose: onCloseInstruction,
  } = useDisclosure();

  const titleLabel = React.useMemo(() => {
    const operationLabel =
      currentProblem.operator === OperatorModel.ADDITION ? "+" : "-";

    return isLargeScreen ? intl.formatMessage({id: 'steps-label', defaultMessage: "Étapes"}) : (`
      ${intl.formatMessage({id: 'solve', defaultMessage: "Résoudre"})} 
      ${intlNumber(currentProblem.operand1, intl.locale)} 
      ${operationLabel} 
      ${intlNumber(currentProblem.operand2, intl.locale)}
    `);
  }, [currentProblem]);

  const infoButtonStyles = {
    w: isLargeScreen ? "26px" : "20px",
    minWidth: isLargeScreen ? "26px" : "20px",
    h: isLargeScreen ? "26px" : "20px",
    ml: "auto",
    cursor: "pointer",
    bg: "transparent",
    p: 0,
    "&:hover": { bg: "transparent" }
  }

  const InstructionBody = () => {
    return (
      <>
        <Flex
          sx={{
            bgColor: "#F5F3EF",
            padding: isLargeScreen ? "10px" : "8px",
            alignItems: "center",
            borderRadius: "8px",
          }}
        >
          <Circle
            size={isLargeScreen ? "26px" : "20px"}
            bg="#E7E1D8"
            color="black"
            sx={{ fontSize: isLargeScreen ? "1.26rem" : "1rem" }}
          >
            1
          </Circle>
          <Text sx={{ ml: isLargeScreen ? "10px" : "8px", fontSize: isLargeScreen ? "1.26rem" : "1rem" }}>
            <FormattedMessage
              id={
                currentProblem.operator === OperatorModel.ADDITION ?
                  "solve-instructions-step-1" : "solve-subtraction-instructions-step-1"
              }
              defaultMessage={"Place ton opération"}
            />
          </Text>
          <Button sx={infoButtonStyles} onClick={onOpenInstruction}>
            <AiOutlineInfoCircle size={isLargeScreen ? "26px" : "20px"} />
          </Button>
        </Flex>
        <Flex
          sx={{
            bgColor: "#F5F3EF",
            padding: isLargeScreen ? "10px" : "8px",
            alignItems: "center",
            borderRadius: "8px",
          }}
        >
          <Circle
            size={isLargeScreen ? "26px" : "20px"}
            bg="#E7E1D8"
            color="black"
            sx={{ fontSize: isLargeScreen ? "1.26rem" : "1rem" }}
          >
            2
          </Circle>
          <Text sx={{ ml: isLargeScreen ? "10px" : "8px", fontSize: isLargeScreen ? "1.26rem" : "1rem" }}>
            <FormattedMessage
              id={
                currentProblem.operator === OperatorModel.ADDITION ?
                  "solve-instructions-step-2" : "solve-subtraction-instructions-step-2"
              }
              defaultMessage={
                currentProblem.operator === OperatorModel.ADDITION ? "Effectue l’addition" : "Effectue la soustraction"
              }
            />
          </Text>
        </Flex>
        <Flex
          sx={{
            bgColor: "#F5F3EF",
            padding: isLargeScreen ? "10px" : "8px",
            alignItems: "center",
            borderRadius: "8px",
          }}
        >
          <Circle
            size={isLargeScreen ? "26px" : "20px"}
            bg="#E7E1D8"
            color="black"
            sx={{ fontSize: isLargeScreen ? "1.26rem" : "1rem" }}
          >
            3
          </Circle>
          <Text sx={{ ml: isLargeScreen ? "10px" : "8px", fontSize: isLargeScreen ? "1.26rem" : "1rem" }}>
            <FormattedMessage
              id={
                currentProblem.operator === OperatorModel.ADDITION ?
                  "solve-instructions-step-3" : "solve-subtraction-instructions-step-3"
              }
              defaultMessage={"Valide ton résultat"}
            />
          </Text>
        </Flex>
        <Flex
          sx={{
            bgColor: "#DEF4FB",
            padding: isLargeScreen ? "10px" : "8px",
            borderRadius: "8px",
            border: "1px solid #94DBF2",
          }}
        >
          <Text sx={{fontSize: isLargeScreen ? "1.26rem" : "1rem" }}>
            <FormattedMessage
              id={
                currentProblem.operator === OperatorModel.ADDITION ?
                  "solve-instructions-step-4" :
                  "solve-subtraction-instructions-step-4"
              }
              defaultMessage={
                currentProblem.operator === OperatorModel.ADDITION ?
                  "N’oublie pas de prendre en compte les retenues." :
                  "Emprunte une dizaine si tu n'as pas assez d'unités."
              }
            />
          </Text>
          <Button sx={infoButtonStyles} onClick={onOpenAssistance}>
            <AiOutlineInfoCircle size={isLargeScreen ? "26px" : "20px"} />
          </Button>
        </Flex>
      </>
    )
  }

  return (
    <Box
      sx={{
        bg: "white",
        borderRadius: isLargeScreen ? "16px" : 0,
        border: isLargeScreen ? "2px solid #F5F3EF" : "2px solid #E7E1D8",
        borderTop: "none",
        borderBottom: "none",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          padding: isLargeScreen ? "12px" : "10px",
          bgColor: "#F5F3EF",
          borderTopLeftRadius: isLargeScreen ? "16px" : 0,
          borderTopRightRadius: isLargeScreen ? "16px" : 0,
          position: "relative"
        }}
      >
        {!isLargeScreen && (
          <APIconButton
            sx={{
              transform: "scale(-1, 1)",
              background: "#E7E1D8",
              boxShadow: "none",
              width: "31px",
              height: "31px",
              padding: 0,
              minWidth: "31px",
              position: "absolute",
              left: "10px",
              top: "7px"
            }}
            onClick={resetExercise}
          >
            <GrPowerReset />
          </APIconButton>
        )}
        <Text sx={{ fontSize: isLargeScreen ? "20px" : "16px", color: "#000", fontWeight: 900, textAlign: "center" }}>
          {titleLabel}
        </Text>
        {!isLargeScreen && (
          <Box sx={{position: "absolute", right: '10px', top: 0, bottom: 0, display: "flex", alignItems: "center"}}>
            <APLink onClick={() => setMobileInstructionOpen(true)}>
              {intl.formatMessage({id: 'steps-label', defaultMessage: "Étapes"})}
            </APLink>
          </Box>
        )}
      </Box>
      {isLargeScreen && (
        <Stack sx={{ padding: "12px" }}>
          <InstructionBody/>
        </Stack>
      )}

      <APCommonDialog
        open={mobileInstructionOpen}
        title={intl.formatMessage({id: 'steps-label', defaultMessage: "Étapes"})}
        onClose={() => setMobileInstructionOpen(false)}
        error={false}
        disableFooter
      >
        <Box sx={{p: '16px'}}>
          <Stack>
            <InstructionBody/>
          </Stack>
          <Box sx={{d: "flex", justifyContent: "center", mt: "32px", mb: "5px"}}>
          </Box>
        </Box>
      </APCommonDialog>

      {instructionOpen ? (
        <Flex
          sx={{
            mt: "auto",
            bgColor: "#DEF4FB",
            padding: isLargeScreen ? "10px" : "8px",
            borderRadius: isLargeScreen ? "0px 0px 16px 16px" : "8px",
            border: "1px solid #94DBF2",
            flexDir: "column",
            position: isLargeScreen ? "static" : "fixed",
            zIndex: isLargeScreen ? 1 : 10001,
            bottom: isLargeScreen ? '60px' : '48px',
            left: isLargeScreen ? '30px' : '24px',
            right: isLargeScreen ? '30px' : '24px'
          }}
        >
          <Flex>
            <Text sx={{ fontWeight: 700, fontSize: isLargeScreen ? "1.26rem" : "1rem" }}>
              <FormattedMessage id="trick" defaultMessage={"Astuce"}/>
            </Text>
            <Box
              sx={{ ml: "auto", cursor: "pointer" }}
              onClick={onCloseInstruction}
            >
              <CgClose size={isLargeScreen ? "26px" : "20px"} />
            </Box>
          </Flex>

          <Text sx={{fontSize: isLargeScreen ? "1.26rem" : "1rem"}}>
            <FormattedMessage
              id={currentProblem.operator === OperatorModel.ADDITION ? "addition-annotation" : "subtraction-annotation"}
              defaultMessage={
                currentProblem.operator === OperatorModel.ADDITION ?
                  "J’écris l’addition en alignant les chiffres de la même position." :
                  "J’écris la soustraction en alignant les chiffres de la même position."
              }
            />
          </Text>
        </Flex>
      ) : null}
    </Box>
  );
}

export default InstructionSidebar;
